import { reportGetters } from '@ltvco/refresh-lib/v1';

import { SEARCH_TYPES } from 'components/DeepSearch/constants';
import { toUSStateAbbreviation } from 'components/DeepSearch/utils/usStateUtils';
import { isUnknown } from 'components/DeepSearch/utils/utils';

import type { ResponseError } from '@ltvco/refresh-lib/utils';
import type { PermalinkOrTeaserOrParamsResult } from 'components/DeepSearch/types/conversationTypes';

export interface VehicleSearchParams {
  state?: string;
  vin?: string;
  licensePlate?: string;
  year?: string;
  make?: string;
  model?: string;
  trim?: string;
}

export interface PropertySearchParams {
  address: string;
  city?: string;
  state?: string;
  zipcode?: string;
}

// Type guard function to check if error is a ResponseError
function isResponseError(error: unknown): error is ResponseError {
  return Boolean(
    error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response !== undefined
  );
}

/**
 * Extracts error message from API response data
 */
function extractErrorMessage(responseData: any): string {
  // Check for detailed error information in various possible locations
  if (responseData?.record_search?.errors?.length > 0) {
    return responseData.record_search.errors.join(', ');
  }

  if (responseData?.report?.errors?.length > 0) {
    return responseData.report.errors.join(', ');
  }

  if (responseData?.errors?.length > 0) {
    return responseData.errors.join(', ');
  }

  if (responseData?.error) {
    return responseData.error;
  }

  if (responseData?.message) {
    return responseData.message;
  }

  if (responseData?.meta?.status) {
    return `Request failed with status ${responseData.meta.status}`;
  }

  // If no specific error details found, return the full response data
  return `Error getting permalink: ${JSON.stringify(responseData)}`;
}

/**
 * Handles errors and extracts meaningful error messages
 */
async function handlePermalinkError(error: unknown): Promise<string> {
  if (isResponseError(error)) {
    try {
      const responseData = await error.response.json();
      return extractErrorMessage(responseData);
    } catch {
      // If JSON parsing fails, fall back to status information
      return `Error getting permalink: ${
        error.response.statusText || error.response.status
      }`;
    }
  }

  const errorMessage = error instanceof Error ? error.message : String(error);
  return `Error getting permalink: ${errorMessage}`;
}

/**
 * Core function to get permalink from a report getter
 */
async function getPermalink(
  // eslint-disable-next-line no-unused-vars
  reportGetter: (params: { queryParams: URLSearchParams }) => Promise<any>,
  queryParams: URLSearchParams
): Promise<PermalinkOrTeaserOrParamsResult> {
  queryParams.set('generator', 'chat');

  try {
    const result = await reportGetter({ queryParams });
    return { permalink: result.report.permalink };
  } catch (error: unknown) {
    const errorMessage = await handlePermalinkError(error);
    return { error: errorMessage };
  }
}

/**
 * Gets permalink for a phone number search
 */
export async function getPhoneNumberPermalink(
  phoneNumber: string
): Promise<PermalinkOrTeaserOrParamsResult> {
  if (!phoneNumber?.trim()) {
    return { error: 'Phone number is required' };
  }

  const reportGetter =
    reportGetters[SEARCH_TYPES.PHONE as keyof typeof reportGetters];
  if (!reportGetter) {
    return { error: 'Phone number search type not supported' };
  }

  try {
    return await getPermalink(
      reportGetter,
      new URLSearchParams(`?phone=${phoneNumber.trim()}`)
    );
  } catch (error: unknown) {
    const errorMessage = await handlePermalinkError(error);
    return { error: errorMessage };
  }
}

/**
 * Gets permalink for an email search
 */
export async function getEmailPermalink(
  email: string
): Promise<PermalinkOrTeaserOrParamsResult> {
  if (!email?.trim()) {
    return { error: 'Email is required' };
  }

  const reportGetter =
    reportGetters[SEARCH_TYPES.EMAIL as keyof typeof reportGetters];
  if (!reportGetter) {
    return { error: 'Email search type not supported' };
  }

  try {
    return await getPermalink(
      reportGetter,
      new URLSearchParams(`?email=${email.trim()}`)
    );
  } catch (error: unknown) {
    const errorMessage = await handlePermalinkError(error);
    return { error: errorMessage };
  }
}

/**
 * Gets permalink for a username search
 */
export async function getUsernamePermalink(
  username: string
): Promise<PermalinkOrTeaserOrParamsResult> {
  if (!username?.trim()) {
    return { error: 'Username is required' };
  }

  const reportGetter =
    reportGetters[SEARCH_TYPES.USERNAME as keyof typeof reportGetters];
  if (!reportGetter) {
    return { error: 'Username search type not supported' };
  }

  try {
    return await getPermalink(
      reportGetter,
      new URLSearchParams(`?username=${username.trim()}`)
    );
  } catch (error: unknown) {
    const errorMessage = await handlePermalinkError(error);
    return { error: errorMessage };
  }
}
/**
 * Gets permalink for a property search
 */
export async function getPropertyPermalink(
  address: string,
  city?: string,
  state?: string,
  zipcode?: string
): Promise<PermalinkOrTeaserOrParamsResult> {
  if (!address?.trim()) {
    return { error: 'Address is required' };
  }

  const reportGetter = reportGetters['property' as keyof typeof reportGetters];
  if (!reportGetter) {
    return { error: 'Property search type not supported' };
  }

  const queryParams = new URLSearchParams();
  queryParams.set('address', address.trim());

  if (city?.trim()) {
    queryParams.set('city', city.trim());
  }
  if (state?.trim()) {
    queryParams.set('state', state.trim());
  }
  if (zipcode?.trim()) {
    queryParams.set('zip_code', zipcode.trim());
  }

  queryParams.set('property', '');
  queryParams.set('h-captcha-response', '');
  queryParams.set('include_ca_data', 'true');

  try {
    return await getPermalink(reportGetter, queryParams);
  } catch (error: unknown) {
    const errorMessage = await handlePermalinkError(error);
    return { error: errorMessage };
  }
}

/**
 * Gets permalink for a vehicle search
 */
export async function getVehiclePermalink(
  params: VehicleSearchParams
): Promise<PermalinkOrTeaserOrParamsResult> {
  const { vin, licensePlate, state, year, make, model, trim } = params;

  const reportGetter =
    reportGetters[SEARCH_TYPES.VEHICLE as keyof typeof reportGetters];
  if (!reportGetter) {
    return { error: 'Vehicle search type not supported' };
  }

  // Validate search parameters
  if (vin?.trim() && !isUnknown(vin)) {
    // VIN search
    const queryParams = new URLSearchParams();
    queryParams.set('vin', vin.trim());
    return await getPermalink(reportGetter, queryParams);
  }

  if (
    licensePlate?.trim() &&
    state?.trim() &&
    !isUnknown(state) &&
    !isUnknown(licensePlate)
  ) {
    // License plate search
    const queryParams = new URLSearchParams();
    queryParams.set('license_plate', licensePlate.trim());

    // If we want to have deep search working for other countries or states, we should update the function bellow
    const stateCode = toUSStateAbbreviation(state);
    if (!stateCode) {
      return { error: 'Invalid state, must be a valid US state' };
    }
    queryParams.set('state', stateCode);
    return await getPermalink(reportGetter, queryParams);
  }

  if (
    year?.trim() &&
    make?.trim() &&
    model?.trim() &&
    trim?.trim() &&
    !isUnknown(year) &&
    !isUnknown(make) &&
    !isUnknown(model)
  ) {
    // Year/make/model/trim search
    const queryParams = new URLSearchParams();
    queryParams.set('year', year.trim());
    queryParams.set('make', make.trim());
    queryParams.set('model', model.trim());

    let trimValue = trim;
    if (isUnknown(trim)) {
      trimValue = 'Base';
    } else {
      trimValue = trim.trim();
    }

    queryParams.set('trim', trimValue);
    return await getPermalink(reportGetter, queryParams);
  }

  return {
    error:
      'Invalid vehicle search parameters. Must provide either VIN, license plate + state, or year + make + model + trim.',
  };
}

export const getPersonPermalink = async (bvid: string) => {
  const reportGetter =
    reportGetters[SEARCH_TYPES.PERSON as keyof typeof reportGetters];
  if (!reportGetter) {
    return { error: 'Person search type not supported' };
  }

  try {
    return await getPermalink(
      reportGetter,
      new URLSearchParams(`?bvid=${bvid}&showPartialAddresses=false`)
    );
  } catch (error: unknown) {
    const errorMessage = await handlePermalinkError(error);
    return { error: errorMessage };
  }
};

/**
 * Gets permalink for an ancestor/genealogy report by FamilySearch ID
 */
export async function getAncestorPermalink(
  familySearchId: string
): Promise<PermalinkOrTeaserOrParamsResult> {
  if (!familySearchId?.trim()) {
    return { error: 'FamilySearch ID is required for ancestor reports' };
  }

  const reportGetter =
    reportGetters[SEARCH_TYPES.ANCESTOR as keyof typeof reportGetters];
  if (!reportGetter) {
    return { error: 'Ancestor search type not supported' };
  }

  try {
    return await getPermalink(
      reportGetter,
      new URLSearchParams(`?family_search_id=${familySearchId.trim()}`)
    );
  } catch (error: unknown) {
    const errorMessage = await handlePermalinkError(error);
    return { error: errorMessage };
  }
}
